import type { StoreApi } from "zustand/vanilla";

import {
  applyGroupSymbolTransition,
  type WorkspaceDraft,
} from "../workspace/groups.slice";
import type {
  GroupId,
  SymbolGroup,
  WorkspaceStore,
  WorkspaceWidget,
} from "../workspace/workspace.types";
import type { GroupRegistryDocument } from "./groupRegistry.document";

const toWorkspaceGroup = (
  group: GroupRegistryDocument["groups"][number],
): SymbolGroup => {
  const mirrored: SymbolGroup = {
    id: group.id,
    colorSlot: group.colorSlot,
    linkState: {
      ...(group.linkState.dateRange
        ? { dateRange: { ...group.linkState.dateRange } }
        : {}),
    },
  };
  if (group.name) {
    mirrored.name = group.name;
  }
  return mirrored;
};

const retainLocalSymbol = (
  draft: WorkspaceDraft,
  widgetId: string,
  widget: WorkspaceWidget,
  symbol: string,
): void => {
  if (widget.kind === "watchlist") {
    widget.localSymbol = symbol;
    return;
  }
  if (widget.kind === "orderTicket") {
    const ticket = draft.tickets[widgetId];
    if (ticket) {
      ticket.localSymbol = symbol;
    }
  }
};

const mirrorLiveDefinitions = (
  draft: WorkspaceDraft,
  document: GroupRegistryDocument,
): Set<GroupId> => {
  const fixedIds = new Set<GroupId>();
  for (const globalGroup of document.groups) {
    const id = globalGroup.id;
    fixedIds.add(id);
    const previousSymbol = draft.groups[id]?.linkState.symbol;
    const nextSymbol = globalGroup.linkState.symbol;
    const mirrored = toWorkspaceGroup(globalGroup);
    if (previousSymbol !== undefined) {
      mirrored.linkState.symbol = previousSymbol;
    }
    draft.groups[id] = mirrored;
    if (previousSymbol !== nextSymbol) {
      applyGroupSymbolTransition(draft, id, nextSymbol);
    }
  }
  return fixedIds;
};

const removeAbsentDefinitions = (
  draft: WorkspaceDraft,
  fixedIds: Set<GroupId>,
): void => {
  for (const rawId of Object.keys(draft.groups)) {
    const id = rawId as GroupId;
    if (fixedIds.has(id)) {
      continue;
    }
    const retainSymbol = draft.groups[id]?.linkState.symbol;
    for (const [widgetId, widget] of Object.entries(draft.widgets)) {
      if (widget.groupId !== id) {
        continue;
      }
      if (retainSymbol) {
        retainLocalSymbol(draft, widgetId, widget, retainSymbol);
      }
      widget.groupId = null;
    }
    delete draft.groups[id];
  }
};

const clearOrphanMembership = (
  draft: WorkspaceDraft,
  fixedIds: Set<GroupId>,
): void => {
  for (const widget of Object.values(draft.widgets)) {
    if (widget.groupId !== null && !fixedIds.has(widget.groupId)) {
      widget.groupId = null;
    }
  }
};

export const mirrorGroupRegistryDocument = (
  store: StoreApi<WorkspaceStore>,
  document: GroupRegistryDocument,
): void => {
  store.setState((draft) => {
    const workspaceDraft = draft as WorkspaceDraft;
    const fixedIds = mirrorLiveDefinitions(workspaceDraft, document);
    removeAbsentDefinitions(workspaceDraft, fixedIds);
    clearOrphanMembership(workspaceDraft, fixedIds);
    return draft;
  });
};
